// Signed-out screens: sign in, apply, accept an invitation, forgot and reset password.
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Button, Field, Input, Select } from '@tatra/ui';
import { type FormEvent, useState } from 'react';
import { AuthFrame, FormError } from '../components.js';
import { errorMessage, post } from '../lib/api.js';
import { useSession } from '../lib/session.js';

type Tokens = { accessToken: string; expiresIn: number };

export function SignInPage() {
  const { config, signedIn } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const login = useMutation({
    mutationFn: () => post<Tokens>('/auth/login', { email, password }),
    onSuccess: (r) => {
      signedIn(r);
      void navigate({ to: '/' });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };
  return (
    <AuthFrame
      title="Sign in"
      intro="Register your customers, follow their progress and track your commission."
      footer={
        config?.selfSignUp ? (
          <>
            New here?{' '}
            <Link to="/apply" className="font-medium text-primary underline">
              Apply to become a partner
            </Link>
          </>
        ) : (
          'Partnerships are by invitation. Ask your relationship manager for an invite.'
        )
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" required>
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="Password" required>
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <FormError message={login.isError ? errorMessage(login.error) : null} />
        <Button type="submit" loading={login.isPending} className="w-full">
          Sign in
        </Button>
        <p className="text-center text-sm">
          <Link to="/forgot" className="text-primary underline">
            Forgot your password?
          </Link>
        </p>
      </form>
    </AuthFrame>
  );
}

export function ApplyPage() {
  const { config, signedIn } = useSession();
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: '',
    kind: 'INDIVIDUAL',
    contactName: '',
    email: '',
    phone: '',
    city: '',
    password: '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });
  const apply = useMutation({
    mutationFn: () => post<Tokens>('/auth/apply', { ...f, phone: f.phone || null, city: f.city || null }),
    onSuccess: (r) => {
      signedIn(r);
      void navigate({ to: '/profile' });
    },
  });
  const closed = config && !config.selfSignUp;
  return (
    <AuthFrame
      title="Become a partner"
      intro="Tell us about your firm. You will add your PAN, bank and RERA details next, and we review everything before you can register customers."
      footer={
        <>
          Already a partner?{' '}
          <Link to="/sign-in" className="font-medium text-primary underline">
            Sign in
          </Link>
        </>
      }
    >
      {closed ? (
        <p className="text-sm text-text-muted">
          We are not taking applications on this page. Please ask your relationship manager for an invitation.
        </p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            apply.mutate();
          }}
          className="space-y-4"
        >
          <Field label="Firm or your name" required>
            {(p) => <Input {...p} value={f.name} onChange={set('name')} minLength={2} required />}
          </Field>
          <Field label="Type of business">
            {(p) => (
              <Select {...p} value={f.kind} onChange={set('kind')}>
                <option value="INDIVIDUAL">Individual agent</option>
                <option value="PROPRIETORSHIP">Proprietorship</option>
                <option value="PARTNERSHIP">Partnership</option>
                <option value="COMPANY">Company</option>
              </Select>
            )}
          </Field>
          <Field label="Contact person" required>
            {(p) => (
              <Input {...p} value={f.contactName} onChange={set('contactName')} minLength={2} required />
            )}
          </Field>
          <Field label="Email" required>
            {(p) => (
              <Input
                {...p}
                type="email"
                autoComplete="username"
                value={f.email}
                onChange={set('email')}
                required
              />
            )}
          </Field>
          <Field label="Phone">
            {(p) => <Input {...p} type="tel" inputMode="tel" value={f.phone} onChange={set('phone')} />}
          </Field>
          <Field label="City">{(p) => <Input {...p} value={f.city} onChange={set('city')} />}</Field>
          <Field label="Choose a password" required help="Use at least 12 characters that are hard to guess.">
            {(p) => (
              <Input
                {...p}
                type="password"
                autoComplete="new-password"
                value={f.password}
                onChange={set('password')}
                required
              />
            )}
          </Field>
          <FormError message={apply.isError ? errorMessage(apply.error) : null} />
          <Button type="submit" loading={apply.isPending} className="w-full">
            Apply
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}

/** Set a password from an e-mailed link: an invitation (accept) or a reset. */
export function TokenPasswordPage({ kind }: { kind: 'accept' | 'reset' }) {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const { signedIn } = useSession();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const send = useMutation({
    mutationFn: () =>
      post<Tokens>(kind === 'accept' ? '/auth/accept' : '/auth/password/reset', { token, password }),
    onSuccess: (r) => {
      signedIn(r);
      void navigate({ to: '/' });
    },
  });
  const mismatch = again !== '' && again !== password;
  return (
    <AuthFrame
      title={kind === 'accept' ? 'Welcome: set your password' : 'Choose a new password'}
      intro={kind === 'accept' ? 'Your invitation is valid for 7 days.' : undefined}
    >
      {!token ? (
        <p role="alert" className="text-sm text-danger">
          This link is incomplete. Open the link from your email again.
        </p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!mismatch) send.mutate();
          }}
          className="space-y-4"
        >
          <Field label="New password" required>
            {(p) => (
              <Input
                {...p}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            )}
          </Field>
          <Field
            label="Type it again"
            required
            error={mismatch ? 'The two passwords do not match' : undefined}
          >
            {(p) => (
              <Input
                {...p}
                type="password"
                autoComplete="new-password"
                value={again}
                onChange={(e) => setAgain(e.target.value)}
                required
              />
            )}
          </Field>
          <FormError message={send.isError ? errorMessage(send.error) : null} />
          <Button type="submit" loading={send.isPending} className="w-full">
            Save and sign in
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState('');
  const send = useMutation({ mutationFn: () => post('/auth/password/forgot', { email }) });
  return (
    <AuthFrame
      title="Reset your password"
      intro="Enter your email and we will send a link if there is an account for it."
      footer={
        <Link to="/sign-in" className="font-medium text-primary underline">
          Back to sign in
        </Link>
      }
    >
      {send.isSuccess ? (
        <p role="status" className="text-sm text-text">
          If that email belongs to a partner account, a reset link is on its way. It works for a short time
          only.
        </p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send.mutate();
          }}
          className="space-y-4"
        >
          <Field label="Email" required>
            {(p) => (
              <Input {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            )}
          </Field>
          <FormError message={send.isError ? errorMessage(send.error) : null} />
          <Button type="submit" loading={send.isPending} className="w-full">
            Send the link
          </Button>
        </form>
      )}
    </AuthFrame>
  );
}
